/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, BrowserRouter as Router } from 'react-router-dom';
import './App.css';
import { useMsal } from '@azure/msal-react';
import MenuIcon from '@mui/icons-material/Menu';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  StyledEngineProvider,
  ThemeProvider,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license';
import {
  type APIPermissions,
  type EnvInfo,
  type RoutePath,
  ScenarioAPIPermissions,
  ToolAPIPermissions,
} from '@poweralpha/common';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import { Slide, ToastContainer } from 'react-toastify';
import { AgentPanelProvider } from './agent/AgentPanelProvider';
import { hasAgentPermission } from './agent/permissions';
import { getEnv } from './api';
import { usePermissions } from './auth';
import { hasFimoConfigAccessPermission } from './fimo/permissions';
import HomePage from './HomePage';
import FIMOIcon from './icons/cash-toggle.svg?react';
import DatabaseIcon from './icons/database-2.svg?react';
import PlantIcon from './icons/factory-building-power-plant.svg?react';
import BatteryIcon from './icons/server-4.svg?react';
import { lazy } from './lazy';
import NotificationCenter from './notifications/NotificationCenter';
import PASupport from './PASupport';
import { toast } from './paToast';
import { queryClient } from './queryClient';
import { Routes } from './react-components/navigate/routes';
import { UserMenu } from './react-components/UserMenu';
import { usePageTitle } from './react-components/usePageTitle';
import { getTheme } from './styles';
import { useAlertToastTooltips } from './useAlertToastTooltips';
import 'react-toastify/dist/ReactToastify.css';
import './toast.css';
import EnvLabel from './EnvLabel';
import SimulationStatusFab from './simulation-status/SimulationStatusFab';

dayjs.extend(utc);

const MUI_PRO_LICENSE_KEY =
  '28295685e74e802ad0b531b1680a795dTz03Nzc2ZDlmZC05ZWZlLTQwMGEtODg3Yi0xYjM4OWVlYTk4NTgsRT0xNzk3MjA2Mzk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTMsS1Y9Mw==';
LicenseInfo.setLicenseKey(MUI_PRO_LICENSE_KEY);

// Lazy load the different UI sections for code splitting
const Database = lazy(() => import('./database/Database.js'));
const PV = lazy(() => import('./pv/PV.js'));
const ESS = lazy(() => import('./ess/ESS.js'));
const Plant = lazy(() => import('./plant/Plant.js'));
const PlantV2 = lazy(() => import('./plant-v2/Plant.js'));
const FIMO = lazy(() => import('./fimo/FIMO.js'));

const appBarStyle: React.CSSProperties = {
  backdropFilter: 'blur(8px)',
  boxShadow: 'none',
  display: 'block',
  zIndex: 1201,
};
const navButtonSx = {
  '&.active': {
    background: '#ffde98',
    color: '#000',
  },
  marginRight: 1,
};

const menuIconSx = { mr: 2, display: { sm: 'none' } };

const mobileMenuSx = {
  display: { xs: 'block', sm: 'none' },
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    width: '100vw',
    backgroundColor: '#002C41',
  },
};

const mobileNavButtonSx = {
  textAlign: 'center',
  color: '#F7C738',
  ...navButtonSx,
};

const container = window !== undefined ? () => document.body : undefined;

const pathElementMap: Partial<Record<RoutePath, React.ReactNode>> = {
  '*': <HomePage />,
  '/db/*': <Database />,
  '/pv/*': <PV />,
  '/ess/costing/*': <ESS />,
  '/plant/*': <Plant />,
  '/plant-v2/*': <PlantV2 />,
  '/fimo/*': <FIMO />,
  '/components/*': <Navigate to={window.location.pathname.replace(/components/g, 'db')} />,
};

const permissionFilter: (
  key: RoutePath,
  permissions: Array<APIPermissions> | undefined,
) => boolean = (key, p) => {
  switch (key) {
    case '/fimo/*':
      return hasFimoConfigAccessPermission(p);
    case '/pv/*':
      return (
        (p?.includes(ToolAPIPermissions.READ_PVSIM) ||
          p?.includes(ToolAPIPermissions.READ_PVCOSTING)) ??
        false
      );
    case '/ess/*':
      return p?.includes(ToolAPIPermissions.READ_ESSCOSTING) ?? false;
    case '/plant/*':
      return p?.includes(ToolAPIPermissions.WRITE_PLANTSIM) ?? false;
    case '/plant-v2/*':
      return p?.includes(ToolAPIPermissions.WRITE_PLANTSIM) ?? false;
    case '/db/*':
      return (
        (p?.includes(ToolAPIPermissions.WRITE_PLANTSIM) ||
          p?.includes(ToolAPIPermissions.READ_ESSCOSTING) ||
          p?.includes(ToolAPIPermissions.READ_PVCOSTING) ||
          p?.includes(ToolAPIPermissions.READ_HVCOSTING) ||
          p?.includes(ToolAPIPermissions.READ_PVSIM) ||
          p?.includes(ScenarioAPIPermissions.WRITE_APPROVAL_ORIGINATION)) ??
        false
      );
    default:
      return true;
  }
};

function App() {
  const permissions = usePermissions();
  const navItems = [
    {
      label: 'Bid Requests',
      url: '/bid-requests',
      icon: <RequestPageIcon height={20} />,
      show: true,
    },
    {
      label: 'PV',
      url: '/pv',
      icon: <SolarPowerIcon />,
      show:
        permissions?.includes(ToolAPIPermissions.READ_PVSIM) ||
        permissions?.includes(ToolAPIPermissions.READ_PVCOSTING),
    },
    {
      label: 'ESS',
      url: '/ess/costing',
      icon: <BatteryIcon height={16} color="primary" />,
      show: permissions?.includes(ToolAPIPermissions.READ_ESSCOSTING),
    },
    {
      label: 'Plant',
      url: '/plant-v2',
      icon: <PlantIcon style={{ paddingLeft: 2 }} height={16} color="primary" />,
      show: permissions?.includes(ToolAPIPermissions.WRITE_PLANTSIM),
    },
    {
      label: 'FIMO',
      url: '/fimo/configurations',
      icon: <FIMOIcon height={19} color="primary" />,
      show: hasFimoConfigAccessPermission(permissions),
    },
    {
      label: 'Database',
      url: '/db',
      icon: <DatabaseIcon height={20} color="primary" />,
      show:
        permissions?.includes(ToolAPIPermissions.WRITE_PLANTSIM) ||
        permissions?.includes(ToolAPIPermissions.READ_ESSCOSTING) ||
        permissions?.includes(ToolAPIPermissions.READ_PVCOSTING) ||
        permissions?.includes(ToolAPIPermissions.READ_HVCOSTING) ||
        permissions?.includes(ToolAPIPermissions.READ_PVSIM) ||
        permissions?.includes(ScenarioAPIPermissions.WRITE_APPROVAL_ORIGINATION),
    },
  ];
  const { instance, accounts } = useMsal();

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [env, setEnv] = useState<EnvInfo>();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>(prefersDarkMode ? 'dark' : 'light');

  const theme = useMemo(() => getTheme(themeMode), [themeMode]);

  const handleLogoutClick = useCallback(async () => {
    await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, [instance]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prevState) => !prevState);
  }, []);

  // Ensure MSAL has an active account once we have at least one
  useEffect(() => {
    const account = accounts?.[0];
    if (!account) return;

    if (!instance.getActiveAccount()) {
      instance.setActiveAccount(account);
    }
  }, [accounts, instance]);

  // Set the user for the Datadog RUM
  useEffect(() => {
    const activeAccount = instance.getActiveAccount() ?? accounts[0];
    if (!activeAccount) return;

    // biome-ignore lint/suspicious/noExplicitAny: datadog stuff
    (window as any).DD_RUM?.setUser({
      email: activeAccount.username,
      name: activeAccount.name,
    });
  }, [instance.getActiveAccount, accounts[0], instance]);

  useEffect(() => {
    if (env) {
      return;
    }

    const activeAccount = instance.getActiveAccount() ?? accounts[0];
    if (!activeAccount) {
      return;
    }

    getEnv()
      .then((res) => setEnv(res))
      .catch((e) => toast.error(`Failed to fetch environment: ${e}`));
  }, [instance.getActiveAccount, accounts, env, instance]);

  usePageTitle('PowerAlpha');
  useAlertToastTooltips();

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <div className="theme">
            <ToastContainer
              position="bottom-left"
              transition={Slide}
              theme={themeMode}
              limit={4}
              stacked
              newestOnTop={false}
              className="alert-toast-container"
              toastClassName="alert-toast"
              closeOnClick
            />
            <ToastContainer
              position="top-right"
              transition={Slide}
              theme={themeMode}
              limit={4}
              stacked
              newestOnTop={false}
              className="notification-toast-container"
              containerId="notification-container"
              closeOnClick={false}
            />
          </div>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Router>
              <AgentPanelProvider enabled={hasAgentPermission(permissions)}>
                <Box
                  sx={{
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <AppBar
                    position="static"
                    style={{
                      ...appBarStyle,
                      backgroundColor: alpha(
                        themeMode === 'light'
                          ? theme.palette.primary.main
                          : darken(theme.palette.background.paper, 0.7),
                        themeMode === 'light' ? 0.9 : 0.8,
                      ),
                    }}
                    component="nav"
                  >
                    <Toolbar variant="dense" disableGutters sx={{ ml: 1 }}>
                      <IconButton
                        component={NavLink}
                        to="/"
                        aria-label="Home"
                        disableRipple
                        sx={{
                          mr: 1,
                          p: 0,
                          borderRadius: 1,
                          '&:hover': { backgroundColor: 'transparent' },
                        }}
                      >
                        <Box
                          component="img"
                          src="/logo.png"
                          alt="PowerAlpha"
                          sx={{
                            width: 72,
                            height: 40,
                            display: 'block',
                            objectFit: 'contain',
                          }}
                        />
                      </IconButton>
                      <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={menuIconSx}
                      >
                        <MenuIcon />
                      </IconButton>
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        {navItems
                          .filter((n) => n.show)
                          .map((navItem) => (
                            <Button
                              component={NavLink}
                              to={navItem.url}
                              color="secondary"
                              size="small"
                              startIcon={navItem.icon}
                              sx={navButtonSx}
                              key={navItem.label}
                            >
                              {navItem.label}
                            </Button>
                          ))}
                      </Box>
                      <EnvLabel envInfo={env} />
                      <div style={{ flex: 1 }} />
                      <Box
                        component="img"
                        src="/bn_logo.png"
                        alt="BrightNight"
                        style={{ opacity: 0.6 }}
                        sx={{
                          width: 'auto',
                          height: 40,
                          objectFit: 'contain',
                          display: 'block',
                          mr: 1,
                        }}
                      />
                      <NotificationCenter />
                      <PASupport />
                      {accounts && accounts.length > 0 && accounts[0].name && (
                        <UserMenu
                          userName={accounts[0].name}
                          prefersDarkMode={prefersDarkMode}
                          onLogout={handleLogoutClick}
                          onThemeChange={setThemeMode}
                        />
                      )}
                    </Toolbar>
                  </AppBar>
                  <Box component="nav">
                    <Drawer
                      container={container}
                      variant="temporary"
                      open={mobileOpen}
                      onClose={handleDrawerToggle}
                      ModalProps={{
                        keepMounted: true,
                      }}
                      sx={mobileMenuSx}
                    >
                      <Toolbar />
                      <List>
                        {navItems
                          .filter((n) => n.show)
                          .map((item) => (
                            <ListItem key={item.label} disablePadding>
                              <ListItemButton
                                sx={mobileNavButtonSx}
                                component={NavLink}
                                to={item.url}
                              >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.label} />
                              </ListItemButton>
                            </ListItem>
                          ))}
                      </List>
                    </Drawer>
                  </Box>
                  <Suspense fallback={<div />}>
                    <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
                      <Routes
                        pathElementMap={pathElementMap}
                        permissionFilter={permissionFilter}
                        reroutes={{ '/ess': '/ess/costing' }}
                      />
                      <SimulationStatusFab />
                    </Box>
                  </Suspense>
                </Box>
              </AgentPanelProvider>
            </Router>
          </LocalizationProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
